class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.setupSubscriptionButtons();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  async getFirstSellingPlan(variantId) {
    try {
      // Get the product handle from the variant ID
      const variantResponse = await fetch(`/variants/${variantId}.js`);
      if (!variantResponse.ok) {
        console.error('Variant fetch failed:', variantResponse.status, await variantResponse.text());
        return null;
      }
      
      const variantData = await variantResponse.json();
     
      // Get the base price (one-time purchase price)
      const basePrice = variantData.price;
      
      // Check for selling plan allocations
      if (variantData.selling_plan_allocations && variantData.selling_plan_allocations.length > 0) {
        const allocation = variantData.selling_plan_allocations[0];
        const planId = allocation.selling_plan_id;
        
        // Calculate price difference if price adjustments exist
        let priceDifference = 0;
        let adjustedPrice = basePrice;
        let discountPercentage = 0;
        
        if (allocation.price_adjustments && allocation.price_adjustments.length > 0) {
          // Calculate the total adjustment
          for (const adjustment of allocation.price_adjustments) {
            if (adjustment.value_type === 'percentage') {
              const adjustmentAmount = (basePrice * adjustment.value) / 100;
              priceDifference += adjustmentAmount;
            } else if (adjustment.value_type === 'fixed_amount') {
              priceDifference += adjustment.value * 100; // Convert to cents
            } else if (adjustment.value_type === 'price') {
              priceDifference = basePrice - adjustment.value;
            }
          }
          
          // Calculate adjusted price and discount percentage
          adjustedPrice = basePrice - priceDifference;
          discountPercentage = Math.round((priceDifference / basePrice) * 100);
        }
        
        return {
          planId,
          basePrice: basePrice / 100, // Convert to dollars
          adjustedPrice: adjustedPrice / 100, // Convert to dollars
          discountPercentage,
          currency: 'USD' // You might want to make this dynamic based on store settings
        };
      }
      
      return null;
    } catch (error) {
      console.error('Error fetching selling plan:', error);
      return null;
    }
  }

  async renderContents(parsedState) {
    const self = this; // Store reference to 'this'
    const isEmpty = parsedState.item_count === 0;
    
    // Update the empty state class on the root element
    if (isEmpty) {
      this.classList.add('is-empty');
    } else {
      this.classList.remove('is-empty');
    }
    
    try {
      // Update the cart items
      if (parsedState.sections) {
        // Update the cart drawer contents
        this.getSectionsToRender().forEach(({ id, selector }) => {
          const sectionElement = selector 
            ? document.querySelector(selector) 
            : document.getElementById(id);
          
          if (sectionElement && parsedState.sections[id]) {
            sectionElement.innerHTML = self.getSectionInnerHTML(parsedState.sections[id], selector);
          }
        });
      }
      
      // Handle empty state content
      const emptyStateElement = this.querySelector('.drawer__inner-empty');
      if (isEmpty && !emptyStateElement) {
        // Create empty state if it doesn't exist and cart is empty
        const emptyStateHTML = `
          <div class="drawer__inner-empty">
            <div class="cart-drawer__warnings center">
              <div class="cart-drawer__empty-content">
                <h2 class="cart__empty-text">${window.cartStrings.empty}</h2>
                <button class="drawer__close" type="button" aria-label="${window.accessibilityStrings.close}">
                  <span class="svg-wrapper">${window.iconCloseSvg || ''}</span>
                </button>
                <a href="${window.routes.all_products_collection_url || '/collections/all'}" class="button">
                  ${window.cartStrings.continue_shopping || 'Continue shopping'}
                </a>
              </div>
            </div>
          </div>`;
        
        const innerElement = this.querySelector('.drawer__inner');
        if (innerElement) {
          innerElement.insertAdjacentHTML('beforeend', emptyStateHTML);
        }
      } else if (!isEmpty && emptyStateElement) {
        // Remove empty state if cart is not empty but empty state exists
        emptyStateElement.remove();
      }
            
      // Set up overlay click handler
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) {
        overlay.addEventListener('click', this.close.bind(this));
      } else {
        console.warn('Cart drawer overlay not found');
      }
      
      this.open();
      
    } catch (error) {
      console.error('Error in renderContents:', error);
      window.location.href = window.location.href.split('?')[0];
    }
  }

  setupSubscriptionButtons() {
    this.addEventListener('click', async (event) => {
      if (event.target.classList.contains('subscription-update')) {
        await this.handleSubscriptionUpdate(event.target);
      }
    });
  }

  async handleSubscriptionUpdate(button) {
    const quantity = parseInt(button.dataset.quantity) || 1;
    const lineIndex = parseInt(button.dataset.lineIndex, 10);
    const sellingPlanId = button.dataset.sellingPlan;
    
    // Disable button during request
    button.disabled = true;
    button.innerHTML = 'Loading...'; // Use innerHTML to preserve any HTML in the button
    
    try {
      // Add the item to cart with the selling plan      
      const payload = {
        line: lineIndex,
        quantity: quantity,
        selling_plan: sellingPlanId
      }
      
      button.innerHTML = 'Updating...';
      
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.description || 'Failed to update subscription');
      }
      
      // Refresh the cart to show updated items
      this.refreshCart();
      
    } catch (error) {
      console.error('Error in subscription update:', error);
      throw error; // Re-throw to be caught by the caller
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  async refreshCart() {
    try {
      // Show loading state
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        cartDrawer.classList.add('loading');
      }

      // Get the current cart state
      const response = await fetch('/cart.js');
      const cartData = await response.json();
      
      // Get the updated cart drawer HTML with cache busting
      const sectionsResponse = await fetch('/?sections=cart-drawer&t=' + Date.now());
      const sectionsData = await sectionsResponse.json();
      
      if (!sectionsData['cart-drawer']) {
        throw new Error('Could not load cart drawer content');
      }

      // Update the cart drawer
      const newCartDrawer = this.getSectionDOM(sectionsData['cart-drawer'], 'cart-drawer');
      if (newCartDrawer) {
        this.innerHTML = newCartDrawer.innerHTML;
        
        // Re-initialize the cart drawer
        this.setupSubscriptionButtons();
        
        // Re-attach event listeners
        this.setHeaderCartIconAccessibility();
        this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
      }

      // Update cart count in the header
      const cartBubble = document.querySelector('#cart-icon-bubble');
      if (cartBubble) {
        const cartBubbleResponse = await fetch('/?section_id=cart-icon-bubble');
        const cartBubbleHTML = await cartBubbleResponse.text();
        const newCartBubble = this.getSectionDOM(cartBubbleHTML, '#shopify-section-cart-icon-bubble');
        if (newCartBubble) {
          cartBubble.innerHTML = newCartBubble.innerHTML;
        }
      }

    } catch (error) {
      console.error('Error refreshing cart:', error);
      
      // Show error state
      const errorMessage = document.createElement('div');
      errorMessage.className = 'cart-drawer__error';
      errorMessage.textContent = 'Unable to update cart. Please refresh the page.';
      this.querySelector('.drawer__inner')?.prepend(errorMessage);
      
      // Remove error after delay
      setTimeout(() => {
        errorMessage.remove();
      }, 5000);
      
    } finally {
      // Remove loading state
      this.classList.remove('loading');
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);